/**
 * useShareLink - generates a shareable deep link via POST /deep-links/create.
 * Returns the URL, loading/error state, and generate/share/copy helpers.
 */

import { useState, useCallback, useRef } from 'react';
import { Share, Platform } from 'react-native';
import { useApiClient } from '../../../helpers/apiClient.helper';
import { useSnackbar } from './useSnackbar';
import { API_ENDPOINTS } from '../../../constants/api';
import type { DeepLinkType } from '../types/deepLink.types';

/**
 * Fallback for mobile web where clipboard API may be restricted (e.g. Safari).
 * Only safe to call when Platform.OS === 'web'.
 */
function copyToClipboardFallback(text: string): void {
  if (Platform.OS !== 'web' || typeof document === 'undefined') return;
  const textarea = document.createElement('textarea');
  textarea.value = text;
  textarea.setAttribute('readonly', '');
  textarea.style.position = 'absolute';
  textarea.style.left = '-9999px';
  document.body.appendChild(textarea);
  textarea.select();
  document.execCommand('copy');
  document.body.removeChild(textarea);
}

interface CreateLinkResponse {
  success: boolean;
  data?: {
    url: string;
    token: string;
    path: string;
    expiresAt: string;
  };
  error?: string;
}

interface UseShareLinkOptions {
  type: DeepLinkType;
  /** Required for group/challenge. Optional for friend (auto-created). */
  targetId?: string;
}

interface UseShareLinkReturn {
  url: string | null;
  loading: boolean;
  error: string | null;
  generateLink: () => Promise<void>;
  shareLink: () => Promise<void>;
  copyLink: () => Promise<void>;
  /** Clear link state (e.g. when modal closes). Call when modal is closed so next open generates fresh. */
  resetLink: () => void;
}

export function useShareLink({ type, targetId }: UseShareLinkOptions): UseShareLinkReturn {
  const apiClient = useApiClient();
  const apiClientRef = useRef(apiClient);
  apiClientRef.current = apiClient;
  const { showSnackbar } = useSnackbar();

  const [url, setUrl] = useState<string | null>(null);
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const generateLink = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      const body: Record<string, string> = { type };
      if (targetId) body.targetId = targetId;

      const response = await apiClientRef.current<CreateLinkResponse>(
        API_ENDPOINTS.deepLinks.create,
        { method: 'POST', data: body }
      );

      if (response.data?.success && response.data?.data) {
        setUrl(response.data.data.url);
      } else {
        setError(response.data?.error || 'Failed to generate link');
      }
    } catch (err) {
      const axiosErr = err as { response?: { data?: { error?: string } } };
      setError(axiosErr?.response?.data?.error || 'Failed to generate link');
    } finally {
      setLoading(false);
    }
  }, [type, targetId]);

  const shareLink = useCallback(async () => {
    if (!url) return;
    try {
      if (Platform.OS === 'web' && typeof navigator !== 'undefined' && navigator.share) {
        await navigator.share({
          title: 'Invite link',
          text: url,
        });
      } else {
        await Share.share({ message: url });
      }
    } catch {
      // User cancelled share or share failed silently
    }
  }, [url]);

  const copyLink = useCallback(async () => {
    if (!url) return;
    try {
      if (Platform.OS === 'web' && typeof document !== 'undefined') {
        // Prefer Clipboard API; fallback for mobile Safari / insecure context
        if (navigator.clipboard && typeof navigator.clipboard.writeText === 'function') {
          await navigator.clipboard.writeText(url);
        } else {
          copyToClipboardFallback(url);
        }
        showSnackbar('Link copied!', 'success');
      } else {
        await Share.share({ message: url });
      }
    } catch {
      showSnackbar('Failed to copy link', 'error');
    }
  }, [url, showSnackbar]);

  const resetLink = useCallback(() => {
    setUrl(null);
    setLoading(false);
    setError(null);
  }, []);

  return { url, loading, error, generateLink, shareLink, copyLink, resetLink };
}
